import { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useProspectDetailsUrl } from './use-prospect-details-url';
import { getProspectDetailsRouteInfo } from '../utils/prospect-details-url';
import { setProspectDetails } from '../../components/prospect/prospect-slice';

type UseProspectDetailsFromUrlOptions = {
  /**
   * Enable/disable URL-driven modal behavior.
   * Useful when the component stays mounted (e.g. tab panes) but should not
   * react to URL changes when not active.
   */
  enabled?: boolean;
  /**
   * Function to send request to get prospect details
   */
  // sendGetSingleProspectDetailsRequest?: (prospectId: number) => void;
  /**
   * Function to reset selected prospect ID
   */
  resetSelectedProspectId?: () => void;
  /**
   * Custom regex pattern to extract prospectId from pathname if useParams doesn't work
   * Default pattern matches: /{basePath}/(\d+)
   */
  pathnamePattern?: RegExp;
};

/**
 * Reusable hook that automatically handles prospect details modal state based on URL
 * Reads URL parameters, opens/closes modal, and manages state
 *
 * @param options - Configuration options
 * @returns Object with modal state and handlers
 */
export const useProspectDetailsFromUrl = (
  options: UseProspectDetailsFromUrlOptions = {},
) => {
  const {
    enabled = true,
    // sendGetSingleProspectDetailsRequest,
    resetSelectedProspectId,
    pathnamePattern,
  } = options;

  const dispatch = useDispatch();
  const { prospectId: prospectIdFromParam, tab: tabParam } = useParams<{
    prospectId?: string;
    tab?: string;
  }>();
  const location = useLocation();
  const {
    tabFromUrl,
    closeProspectDetails,
    openProspectDetails,
  } = useProspectDetailsUrl();

  const [selectedProspectId, setSelectedProspectId] = useState<number | null>(
    null,
  );
  const [
    showProspectDetailsModal,
    setShowProspectDetailsModal,
  ] = useState<boolean>(false);

  // Determine default tab from URL
  const defaultTab = useMemo(() => {
    // If tab is explicitly in URL, use it
    if (
      tabParam === 'activity' ||
      tabParam === 'sequences' ||
      tabParam === 'contacts' ||
      tabParam === 'notes' ||
      tabParam === 'tasks' ||
      tabParam === 'email'
    ) {
      return tabParam as any;
    }

    // If tab is in pathname, use it
    if (tabFromUrl) {
      return tabFromUrl;
    }

    // Otherwise, determine default based on route type
    const routeInfo = getProspectDetailsRouteInfo(location.pathname);
    // Tasks route defaults to 'contacts', others default to 'activity'
    return routeInfo.viewType === 'tasks' ? 'contacts' : 'activity';
  }, [tabParam, tabFromUrl, location.pathname]);

  // Handle prospect page URL Values - read from URL params
  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Extract prospectId from URL params or pathname as fallback
    let prospectId: number | null = null;

    if (prospectIdFromParam) {
      prospectId = Number(prospectIdFromParam) || null;
    } else if (pathnamePattern) {
      // Use custom pattern if provided
      const pathMatch = location.pathname.match(pathnamePattern);
      if (pathMatch) {
        prospectId = Number(pathMatch[1]) || null;
      }
    } else {
      // Default fallback: try to extract from pathname directly
      // Matches patterns like: /leads/123, /tasks/123, /prospects/list/123, /conversations/:hashId/123, /sequence/:sequenceId/(prospects|emails)/123, etc.
      // For conversations, hashId can be alphanumeric, so we need a special pattern
      const conversationsMatch = location.pathname.match(
        /^\/conversations\/[^/]+\/(\d+)/,
      );
      if (conversationsMatch) {
        prospectId = Number(conversationsMatch[1]) || null;
      } else {
        // For other routes - dynamically matches /v2/leads/:tab/:subtab/:prospectId pattern
        const pathMatch = location.pathname.match(
          /(?:\/v2\/leads\/[^/]+\/[^/]+|\/leads|\/tasks|\/conversations|\/crm\/(?:list|kanban)|\/sequence\/\d+\/(?:prospects|emails))\/(\d+)/,
        );
        if (pathMatch) {
          prospectId = Number(pathMatch[1]) || null;
        }
      }
    }

    if (prospectId && !Number.isNaN(prospectId)) {
      setSelectedProspectId(prospectId);
      // Dispatch Redux action to set prospect details
      dispatch(setProspectDetails(prospectId));
      // Call custom request handler if provided
      // if (sendGetSingleProspectDetailsRequest) {
      //   sendGetSingleProspectDetailsRequest(prospectId);
      // }
      setShowProspectDetailsModal(true);
    } else {
      // If no prospectId in URL, close modal and reset
      setShowProspectDetailsModal(false);
      if (selectedProspectId !== null) {
        setSelectedProspectId(null);
      }
      if (resetSelectedProspectId) {
        resetSelectedProspectId();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, prospectIdFromParam, location.pathname]);

  const hideShowProspectDetailsModal = () => {
    // Update URL to remove prospectId and tab when closing modal
    closeProspectDetails();
    setShowProspectDetailsModal(false);
    setSelectedProspectId(null);
    if (resetSelectedProspectId) {
      resetSelectedProspectId();
    }
  };

  const openProspectDetailsModal = (prospectId: number, tab?: string) => {
    if (!prospectId || Number.isNaN(prospectId)) {
      return;
    }

    // Update URL with prospectId and tab
    openProspectDetails(prospectId, tab as any);

    // Update local state
    setSelectedProspectId(prospectId);
    // Dispatch Redux action to set prospect details
    dispatch(setProspectDetails(prospectId));
    // Call custom request handler if provided
    // if (sendGetSingleProspectDetailsRequest) {
    //   sendGetSingleProspectDetailsRequest(prospectId);
    // }
    setShowProspectDetailsModal(true);
  };

  return {
    selectedProspectId,
    showProspectDetailsModal,
    defaultTab,
    hideShowProspectDetailsModal,
    openProspectDetailsModal,
    setSelectedProspectId,
    setShowProspectDetailsModal,
  };
};
